/**
 * @fileoverview Actor display config for `ChangeHistory` entries.
 *
 * Split out of `ChangeHistory.tsx` (react-refresh/only-export-components) —
 * pure classification of a history entry's actor into an icon/label/color,
 * kept separate from the collapsible audit-trail renderer.
 */

import {
  Bot,
  Database,
  Merge,
  Sparkles,
  StickyNote,
  User as UserIcon,
} from "lucide-react";
import { getDisplayName } from "@/ui/composed/user-avatar.helpers";
import type { BadgeColor } from "@/ui/primitives/badge";
import type { Id } from "@cvx/_generated/dataModel";
import type { ChangeHistoryEntry, ResolvedHistoryUser } from "./ChangeHistory";

interface ActorInfo {
  /** Icon component for non-user (system / agent / dedup / etc.) entries. */
  Icon: typeof Bot;
  label: string;
  badgeColor: BadgeColor;
  bgColor: string;
  iconColor: string;
  /** Set when the entry resolves to a real user — drives avatar rendering. */
  user?: ResolvedHistoryUser;
  /**
   * True when the entry was applied through the chat agent on behalf of
   * the practitioner. Detected via a `(via Morgan)` suffix on the actor
   * string written by `convex/agents/universal/proposals.ts`.
   */
  viaMorgan?: boolean;
}

const VIA_MORGAN_SUFFIX = " (via Morgan)";

export function getActorInfo(
  entry: ChangeHistoryEntry,
  usersById?: Map<Id<"users">, ResolvedHistoryUser>,
): ActorInfo {
  const { action, userId } = entry;
  // Strip the agent-mediated suffix so the rest of the dispatch sees
  // the practitioner's bare name. The suffix is re-surfaced via the
  // `viaMorgan` flag for the renderer.
  const rawActor = entry.actor ?? "";
  const viaMorgan = rawActor.endsWith(VIA_MORGAN_SUFFIX);
  const actor = viaMorgan
    ? rawActor.slice(0, -VIA_MORGAN_SUFFIX.length).trim()
    : rawActor;

  // Note actions are always taken by a human practitioner. Surface them
  // with a dedicated icon + label so they're easy to scan in a long
  // history feed dominated by enrichment / dedup entries.
  if (
    action === "note_added" ||
    action === "note_edited" ||
    action === "note_deleted"
  ) {
    const label =
      action === "note_added"
        ? "Note added"
        : action === "note_edited"
          ? "Note edited"
          : "Note deleted";
    return {
      Icon: StickyNote,
      label,
      badgeColor: "emerald",
      bgColor: "bg-emerald-50 dark:bg-emerald-950/30",
      iconColor: "text-emerald-600 dark:text-emerald-400",
      user: userId ? usersById?.get(userId) : undefined,
    };
  }
  // Bare `"system"` (no colon) is what the contacts reconcile pipeline
  // writes today; the colon-prefixed form is the older convention used
  // elsewhere. Both should render the same "System" badge so the audit
  // trail doesn't show a raw lowercase `"system"` actor name.
  if (actor === "system" || actor.startsWith("system:")) {
    return {
      Icon: Database,
      label: "System",
      badgeColor: "sky",
      bgColor: "bg-sky-50 dark:bg-sky-950/30",
      iconColor: "text-sky-600 dark:text-sky-400",
    };
  }
  if (
    actor.startsWith("agent:dedup") ||
    actor === "dedup_agent" ||
    action === "merged"
  ) {
    return {
      Icon: Merge,
      label: "Dedup",
      badgeColor: "violet",
      bgColor: "bg-violet-50 dark:bg-violet-950/30",
      iconColor: "text-violet-600 dark:text-violet-400",
    };
  }
  // Contact enrichment agent: action carries the agent identity (the
  // human approver is stored as `actor`), so check `action` first.
  if (action.startsWith("enriched_from_")) {
    return {
      Icon: Sparkles,
      label: action === "enriched_from_web" ? "AI · web" : "AI · sources",
      badgeColor: "amber",
      bgColor: "bg-amber-50 dark:bg-amber-950/30",
      iconColor: "text-amber-600 dark:text-amber-400",
    };
  }
  if (actor.startsWith("agent:enrichment") || actor === "review_agent") {
    return {
      Icon: Sparkles,
      label: "Enriched",
      badgeColor: "amber",
      bgColor: "bg-amber-50 dark:bg-amber-950/30",
      iconColor: "text-amber-600 dark:text-amber-400",
    };
  }
  if (actor.startsWith("worker:") || actor.startsWith("agent:")) {
    return {
      Icon: Bot,
      label: "Agent",
      badgeColor: "blue",
      bgColor: "bg-blue-50 dark:bg-blue-950/30",
      iconColor: "text-blue-600 dark:text-blue-400",
    };
  }

  // Human / practitioner. Prefer the resolved user record (full name +
  // avatar) when present; fall back to the legacy free-text `actor` string
  // for old entries written before the userId foreign key existed.
  const resolved = userId ? usersById?.get(userId) : undefined;
  const label = resolved
    ? getDisplayName(resolved.firstName, resolved.lastName, resolved.email) ||
      "Practitioner"
    : actor || "Practitioner";
  return {
    Icon: UserIcon,
    label,
    badgeColor: "emerald",
    bgColor: "bg-emerald-50 dark:bg-emerald-950/30",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    user: resolved,
    viaMorgan,
  };
}
